import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Badge, EmptyState, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import {
  createCadence,
  listCadences,
  listEnrollments,
  removeCadence,
  stopEnrollment,
  updateCadence,
  type Cadence,
  type CadenceStep,
  type CadenceStepKind,
} from './api';

/**
 * Prospecting cadences (Phase 122).
 *
 * A rep contacts a lead twice, hears nothing and moves on; the deals that close are the ones
 * somebody touched six times over three weeks. This screen is where that sequence is written down.
 *
 * The editor shows the days as **absolute offsets from the start** — day 0, day 3, day 7 — because
 * that is how anybody plans a cadence out loud. Relative gaps ("three days after the last one") are
 * the same information and nobody can read the shape of the sequence from them.
 */

const STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'info',
  FINISHED: 'positive',
  STOPPED: 'neutral',
  FAILED: 'danger',
};

type Draft = Omit<CadenceStep, 'position'>;

const NEW_STEP: Draft = {
  dayOffset: 3,
  kind: 'TASK',
  subject: '',
  body: '',
  channel: 'CALL',
};

export function CadencesPage(): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const cadences = useQuery({ queryKey: ['cadences'], queryFn: listCadences });

  const settled = (): void => {
    setError(null);
    void client.invalidateQueries({ queryKey: ['cadences'] });
  };

  const pause = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      updateCadence(input.id, { isActive: input.isActive }),
    onSuccess: settled,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const drop = useMutation({
    mutationFn: (id: string) => removeCadence(id),
    onSuccess: settled,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <div className="view">
      <PageHeader title={t('cadences.title')} description={t('cadences.description')} />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <NewCadence onCreated={settled} onError={setError} />

      {cadences.isLoading ? (
        <Panel>
          <p className="muted">{t('state.loading')}</p>
        </Panel>
      ) : (cadences.data?.cadences?.length ?? 0) === 0 ? (
        <Panel>
          <EmptyState title={t('cadences.emptyTitle')} description={t('cadences.emptyBody')} />
        </Panel>
      ) : (
        cadences.data!.cadences!.map((cadence) => (
          <Panel
            key={cadence.id}
            title={cadence.name}
            description={cadence.description ?? undefined}
            actions={
              <span className="cell-inline">
                <Badge tone={cadence.isActive ? 'positive' : 'neutral'}>
                  {t(cadence.isActive ? 'cadences.active' : 'cadences.paused')}
                </Badge>

                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => pause.mutate({ id: cadence.id, isActive: !cadence.isActive })}
                >
                  {t(cadence.isActive ? 'cadences.pause' : 'cadences.resume')}
                </button>

                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => setOpen(open === cadence.id ? null : cadence.id)}
                >
                  {t('cadences.who')}
                </button>

                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => drop.mutate(cadence.id)}
                >
                  {t('cadences.remove')}
                </button>
              </span>
            }
          >
            <Steps cadence={cadence} />

            {open === cadence.id && <Enrollments cadenceId={cadence.id} onError={setError} />}
          </Panel>
        ))
      )}
    </div>
  );
}

function Steps({ cadence }: { cadence: Cadence }): React.JSX.Element {
  return (
    <ol className="cadence-steps">
      {cadence.steps.map((step) => (
        <li key={step.position}>
          <span className="cadence-steps__day">
            {step.dayOffset === 0
              ? t('cadences.day0')
              : t('cadences.day').replace('{n}', String(step.dayOffset))}
          </span>

          <span className="cadence-steps__what">
            <span className="strong">{step.subject}</span>
            <span className="muted small">{step.body}</span>
          </span>

          <Badge tone={step.kind === 'EMAIL' ? 'info' : 'neutral'}>
            {step.kind === 'EMAIL'
              ? t('cadences.kind.EMAIL')
              : t(`cadences.channel.${step.channel ?? 'OTHER'}`)}
          </Badge>
        </li>
      ))}
    </ol>
  );
}

function Enrollments({
  cadenceId,
  onError,
}: {
  cadenceId: string;
  onError: (message: string) => void;
}): React.JSX.Element {
  const client = useQueryClient();

  const enrollments = useQuery({
    queryKey: ['cadence-enrollments', cadenceId],
    queryFn: () => listEnrollments(cadenceId),
  });

  const stop = useMutation({
    mutationFn: (id: string) => stopEnrollment(id),
    onSuccess: () =>
      void client.invalidateQueries({ queryKey: ['cadence-enrollments', cadenceId] }),
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  const rows = enrollments.data?.enrollments ?? [];

  if (rows.length === 0) return <p className="muted small">{t('cadences.nobody')}</p>;

  return (
    <ul className="cadence-people">
      {rows.map((row) => (
        <li key={row.id}>
          <Badge tone={STATUS_TONE[row.status] ?? 'neutral'}>
            {t(`cadences.status.${row.status}`)}
          </Badge>

          <span className="muted small">
            {t('cadences.onStep').replace('{n}', String(row.lastStep))}
            {row.stopReason && ` · ${row.stopReason}`}
          </span>

          {row.status === 'ACTIVE' && (
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => stop.mutate(row.id)}
            >
              {t('cadences.stop')}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function NewCadence({
  onCreated,
  onError,
}: {
  onCreated: () => void;
  onError: (message: string | null) => void;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [steps, setSteps] = useState<Draft[]>([
    { dayOffset: 0, kind: 'EMAIL', subject: '', body: '', channel: null },
  ]);

  const create = useMutation({
    mutationFn: () => createCadence({ name, steps }),
    onSuccess: () => {
      onError(null);
      setName('');
      setSteps([{ dayOffset: 0, kind: 'EMAIL', subject: '', body: '', channel: null }]);
      onCreated();
    },
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  function edit(index: number, changes: Partial<Draft>): void {
    setSteps((current) =>
      current.map((step, position) => (position === index ? { ...step, ...changes } : step)),
    );
  }

  const ready =
    name.trim() !== '' && steps.every((step) => step.subject.trim() && step.body.trim());

  return (
    <Panel title={t('cadences.new')}>
      <div className="field">
        <label className="field__label" htmlFor="cadence-name">
          {t('cadences.name')}
        </label>
        <input
          id="cadence-name"
          className="input"
          value={name}
          maxLength={160}
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <ol className="cadence-editor">
        {steps.map((step, index) => (
          <li key={index}>
            <div className="cadence-editor__row">
              <label className="field field--narrow">
                <span className="field__label">{t('cadences.dayLabel')}</span>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={365}
                  value={step.dayOffset}
                  onChange={(event) => edit(index, { dayOffset: Number(event.target.value) })}
                />
              </label>

              <label className="field field--narrow">
                <span className="field__label">{t('cadences.kindLabel')}</span>
                <select
                  className="select"
                  value={step.kind}
                  onChange={(event) =>
                    edit(index, {
                      kind: event.target.value as CadenceStepKind,
                      channel: event.target.value === 'EMAIL' ? null : 'CALL',
                    })
                  }
                >
                  <option value="EMAIL">{t('cadences.kind.EMAIL')}</option>
                  <option value="TASK">{t('cadences.kind.TASK')}</option>
                </select>
              </label>

              {/* Only a task has a channel: an e-mail step is the channel. */}
              {step.kind === 'TASK' && (
                <label className="field field--narrow">
                  <span className="field__label">{t('cadences.channelLabel')}</span>
                  <select
                    className="select"
                    value={step.channel ?? 'CALL'}
                    onChange={(event) => edit(index, { channel: event.target.value })}
                  >
                    {['CALL', 'WHATSAPP', 'LINKEDIN', 'OTHER'].map((value) => (
                      <option key={value} value={value}>
                        {t(`cadences.channel.${value}`)}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={steps.length === 1}
                onClick={() => setSteps((current) => current.filter((_, at) => at !== index))}
              >
                {t('cadences.removeStep')}
              </button>
            </div>

            <input
              className="input"
              placeholder={t(step.kind === 'EMAIL' ? 'cadences.subject' : 'cadences.taskTitle')}
              value={step.subject}
              maxLength={200}
              onChange={(event) => edit(index, { subject: event.target.value })}
            />

            <textarea
              className="input"
              rows={3}
              placeholder={t(step.kind === 'EMAIL' ? 'cadences.body' : 'cadences.script')}
              value={step.body}
              maxLength={8000}
              onChange={(event) => edit(index, { body: event.target.value })}
            />
          </li>
        ))}
      </ol>

      <p className="muted small">{t('cadences.placeholders')}</p>

      <div className="cell-inline">
        <button
          type="button"
          className="button button--quiet button--compact"
          onClick={() => setSteps((current) => [...current, { ...NEW_STEP }])}
        >
          {t('cadences.addStep')}
        </button>

        <button
          type="button"
          className="button button--primary"
          disabled={!ready || create.isPending}
          onClick={() => create.mutate()}
        >
          {t('cadences.create')}
        </button>
      </div>
    </Panel>
  );
}
